(() => {
  const page = document.querySelector('.tis-reviews-page');
  const summaries = [...document.querySelectorAll('[data-review-summary]')];
  if (!page && summaries.length === 0) return;

  const controls = page?.querySelector('[data-review-controls]');
  const list = page?.querySelector('[data-review-list]');
  let cards = [...(page?.querySelectorAll('[data-review-card]') || [])];
  const publicToken = page?.dataset.reviewPublicToken;
  const shopDomain = page?.dataset.reviewShop;
  let complete = false;
  let loading = false;
  let failed = false;
  let visibleLimit = 12;
  const more = page?.querySelector('[data-review-more]');
  const retry = page?.querySelector('[data-review-retry]');

  const text = (value) => String(value ?? '');
  const formatDate = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.valueOf()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const syncSummaryHistograms = () => {
    summaries.forEach((summary) => {
      const sourceData = summary.querySelector('[data-review-source-data]');
      if (!sourceData) return;
      if (complete && page?.contains(summary)) {
        const total = cards.length;
        const average = total ? (cards.reduce((sum, card) => sum + Number(card.dataset.reviewRating), 0) / total).toFixed(1) : '0.0';
        const score = summary.querySelector('.tis-review-proof__score strong');
        const count = summary.querySelector('.tis-review-proof__score small');
        if (score) score.textContent = average;
        if (count) count.textContent = `${total} reviews`;
        summary.setAttribute('aria-label', `${average} out of 5 stars from ${total} reviews`);
        summary.dataset.reviewCount = total;
      }

      for (const rating of ['5', '4', '3', '2', '1']) {
        const sourceRow = sourceData.querySelector(`[data-rating="${rating}"]`);
        const targetRow = summary.querySelector(`[data-rating="${rating}"]`);
        if (!sourceRow || !targetRow) continue;

        const frequency = complete && page?.contains(summary) ? cards.filter(card => card.dataset.reviewRating === rating).length : sourceRow.getAttribute('data-frequency') || '0';
        const percent = complete && page?.contains(summary) ? (cards.length ? Math.round(Number(frequency) * 100 / cards.length) : 0) : sourceRow.getAttribute('data-percentage') || '0';
        const track = targetRow.querySelector('.tis-review-proof__histogram-track > span');
        const label = targetRow.querySelector('.tis-review-proof__histogram-percent');

        targetRow.dataset.frequency = frequency;
        targetRow.setAttribute('aria-label', `${frequency} reviews, ${percent}%`);
        if (track) track.style.width = `${percent}%`;
        if (label) label.textContent = `${percent}%`;
      }
    });
  };

  const makeCard = (review) => {
    const card = document.createElement('article');
    card.className = 'tis-review-card';
    card.dataset.reviewCard = '';
    card.dataset.reviewRating = String(review.rating);
    const badges = review.transparency_badges || [];
    const source = badges.includes('review_written_in_etsy') ? 'etsy' : badges.includes('review_collected_via_store_invitation') || badges.includes('review_written_in_store') ? 'store' : 'other';
    card.dataset.reviewSource = source;

    const topLine = document.createElement('div');
    topLine.className = 'tis-review-card__topline';
    const name = document.createElement('span');
    name.className = 'tis-review-card__name';
    const names = text(review.reviewer_name || 'Customer').trim().split(/\s+/);
    name.textContent = names.length > 1 ? `${names[0]} ${names.at(-1).slice(0, 1)}.` : names[0];
    topLine.append(name);
    if (source !== 'other') {
      const source = document.createElement('span');
      source.className = 'tis-review-card__source';
      source.textContent = card.dataset.reviewSource === 'etsy' ? 'Etsy review' : 'Our store';
      topLine.append(source);
    }
    card.append(topLine);

    const rating = document.createElement('div');
    rating.className = 'tis-review-card__rating';
    rating.setAttribute('aria-label', `${review.rating} out of 5 stars`);
    rating.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    card.append(rating);

    const body = document.createElement('p');
    body.className = 'tis-review-card__body';
    body.textContent = text(review.body);
    card.append(body);

    const footer = document.createElement('div');
    footer.className = 'tis-review-card__footer';
    const date = document.createElement('time');
    date.dateTime = text(review.created_at);
    date.textContent = formatDate(review.created_at);
    footer.append(date);
    if (review.product_title) {
      const product = document.createElement('span');
      product.title = text(review.product_title);
      product.textContent = text(review.product_title);
      footer.append(product);
    }
    card.append(footer);
    if (review.reply_content) {
      const reply = document.createElement('div');
      reply.className = 'tis-review-card__reply';
      const label = document.createElement('strong');
      label.textContent = 'A note from the studio';
      const body = document.createElement('p');
      body.textContent = new DOMParser().parseFromString(text(review.reply_content), 'text/html').body.textContent;
      reply.append(label, body);
      card.append(reply);
    }
    const pictures = (review.pictures_urls || []).filter(url => typeof url === 'string' && /^https:\/\//.test(url));
    if (pictures.length) {
      const media = document.createElement('div');
      media.className = 'tis-review-card__photos';
      pictures.forEach((url, index) => {
        const link = document.createElement('a');
        link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
        link.setAttribute('aria-label', `Open customer photo ${index + 1}`);
        const image = document.createElement('img');
        image.src = url; image.alt = 'Photo shared with this review'; image.loading = 'lazy'; image.width = 96; image.height = 96;
        link.append(image); media.append(link);
      });
      card.append(media);
    }
    return card;
  };

  const sync = () => {
    syncSummaryHistograms();
    const rating = new URLSearchParams(window.location.search).get('rating');
    const selectedRating = rating && /^[1-5]$/.test(rating) ? rating : 'all';
    const source = new URLSearchParams(window.location.search).get('source');
    const selectedSource = ['etsy', 'store'].includes(source) ? source : 'all';

    controls?.querySelectorAll('[data-review-rating]').forEach((link) => {
      link.classList.toggle('is-active', link.dataset.reviewRating === selectedRating);
      if (link.dataset.reviewRating === selectedRating) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });

    controls?.querySelectorAll('[data-review-source-filter]').forEach(link => {
      const active = link.dataset.reviewSourceFilter === selectedSource;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current');
    });
    const matches = cards.filter(card => (selectedRating === 'all' || card.dataset.reviewRating === selectedRating) && (selectedSource === 'all' || card.dataset.reviewSource === selectedSource));
    const visible = new Set(matches.slice(0, visibleLimit));
    cards.forEach(card => { card.hidden = !visible.has(card); });
    const shown = cards.filter((card) => !card.hidden).length;
    const availability = page?.querySelector('[data-review-availability]');
    if (availability) availability.textContent = complete
      ? `Showing ${shown} of ${matches.length} matching reviews · ${cards.length} published reviews in total.`
      : failed ? `We couldn't load all reviews. Showing ${shown} of ${cards.length} available reviews. Please try again.`
      : loading ? 'Loading the full review collection… You can read the available reviews below.'
      : `Showing ${shown} of ${cards.length} reviews available on this page. The rating summary covers all store reviews.`;
    const empty = page?.querySelector('[data-review-empty]');
    if (empty) empty.hidden = shown > 0;
    if (more) more.hidden = matches.length <= visibleLimit;
    if (retry) { retry.hidden = !failed; retry.disabled = loading; }
  };

  const renderFeed = (reviews) => {
    if (!list) return;
    list.replaceChildren(...reviews.map(makeCard));
    cards = [...list.querySelectorAll('[data-review-card]')];
    sync();
  };

  const loadFeed = async () => {
    if (!publicToken || !shopDomain || !list || loading) return;
    loading = true; failed = false; sync();
    try {
      const getPage = async (type, number) => {
        const url = new URL('https://api.judge.me/api/v1/widgets/all_reviews_page');
        // Public widget token is intended for browser use. Query auth avoids the
        // provider's unsupported cross-origin OPTIONS preflight for custom headers.
        Object.entries({shop_domain: shopDomain, api_token: publicToken, review_type: type, page: number, json_request: true}).forEach(([key, value]) => url.searchParams.set(key, value));
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(url, {headers: {Accept: 'application/json'}, credentials: 'omit', signal: controller.signal});
          if (!response.ok) throw new Error('Review service unavailable');
          const result = await response.json();
          if (!Array.isArray(result.reviews) || !Number.isInteger(result.total_pages) || result.total_pages < 0 || result.total_pages > 200 || result.current_page !== number) throw new Error('Invalid review page');
          return result;
        } finally { clearTimeout(timer); }
      };
      const streams = await Promise.all(['product-reviews', 'shop-reviews'].map(async type => {
        const first = await getPage(type, 1);
        const reviews = [...first.reviews];
        for (let number = 2; number <= first.total_pages; number++) {
          const next = await getPage(type, number);
          if (next.total_pages !== first.total_pages) throw new Error('Review collection changed; retry');
          reviews.push(...next.reviews);
        }
        return reviews;
      }));
      const unique = new Map();
      streams.flat().forEach(review => {
        if (!review.uuid || !Number.isInteger(review.rating) || review.rating < 1 || review.rating > 5) throw new Error('Invalid review');
        unique.set(review.uuid, review);
      });
      const reviews = [...unique.values()].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
      if (!reviews.length && Number(page.querySelector('[data-review-count]')?.dataset.reviewCount) > 0) throw new Error('Review collection is incomplete');
      complete = true;
      renderFeed(reviews);
    } catch {
      failed = true;
    } finally {
      loading = false; sync();
    }
  };

  controls?.addEventListener('click', (event) => {
    const link = event.target.closest('[data-review-rating], [data-review-source-filter]');
    if (!link) return;
    event.preventDefault();
    const url = new URL(window.location.href);
    const key = link.hasAttribute('data-review-rating') ? 'rating' : 'source';
    const value = key === 'rating' ? link.dataset.reviewRating : link.dataset.reviewSourceFilter;
    if (value === 'all') url.searchParams.delete(key); else url.searchParams.set(key, value);
    window.history.pushState({}, '', url);
    visibleLimit = 12;
    sync();
  });
  more?.addEventListener('click', () => { visibleLimit += 12; sync(); });
  retry?.addEventListener('click', loadFeed);
  window.addEventListener('popstate', () => { visibleLimit = 12; sync(); });
  sync();
  loadFeed();
})();
